import React from "react";
import {
  Box,
  Button,
  Badge,
  Chip,
  Divider,
  IconButton,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import type { SessionContentProps } from "./types";

const SessionContentSection: React.FC<SessionContentProps> = ({
  clientObjective,
  keyInsights,
  sessionNotes,
  methods,
  availableMethods,
  methodsLoading,
  onClientObjectiveChange,
  onKeyInsightsChange,
  onSessionNotesChange,
  onSave,
  onToggleMethod,
  onAddMethod,
  onOpenAttachments,
  sessionAttachments,
  readOnly,
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        p: 2.5,
        bgcolor: "#fff",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          }}
        >
          <Box>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.sessionDetails.clientObjective")}
            </Typography>
            <TextField
              value={clientObjective}
              onChange={(e) => onClientObjectiveChange(e.target.value)}
              onBlur={() => onSave?.()}
              placeholder={
                t(
                  "coachingNavigator.sessionDetails.clientObjectivePlaceholder",
                ) || ""
              }
              multiline
              rows={4}
              fullWidth
              disabled={readOnly}
              sx={{
                mt: 1,
                "& .MuiInputBase-inputMultiline": {
                  overflowY: "auto !important",
                },
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.sessionDetails.keyInsights")}
            </Typography>
            <TextField
              value={keyInsights}
              onChange={(e) => onKeyInsightsChange(e.target.value)}
              onBlur={() => onSave?.()}
              placeholder={
                t("coachingNavigator.sessionDetails.keyInsightsPlaceholder") ||
                ""
              }
              multiline
              rows={4}
              fullWidth
              disabled={readOnly}
              sx={{
                mt: 1,
                "& .MuiInputBase-inputMultiline": {
                  overflowY: "auto !important",
                },
              }}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            {t("coachingNavigator.sessionDetails.sessionNotes")}
          </Typography>
          <Box sx={{ position: "relative", mt: 1 }}>
            <TextField
              value={sessionNotes}
              onChange={(e) => onSessionNotesChange(e.target.value)}
              onBlur={() => onSave?.()}
              placeholder={
                t("coachingNavigator.sessionDetails.sessionNotesPlaceholder") ||
                ""
              }
              multiline
              rows={4}
              fullWidth
              disabled={readOnly}
              sx={{
                mt: 1,
                "& .MuiInputBase-inputMultiline": {
                  overflowY: "auto !important",
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                display: "flex",
                gap: 1,
              }}
            >
              <Badge color="primary" badgeContent={sessionAttachments || 0} overlap="circular">
                <IconButton size="small" color="default" onClick={onOpenAttachments}>
                  <AttachFileOutlinedIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.sessionDetails.methodsCovered")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {methodsLoading ? (
                <Chip
                  label={t("coachingNavigator.methods.loading")}
                  variant="outlined"
                  size="small"
                />
              ) : availableMethods.length > 0 ? (
                availableMethods.map((method) =>
                  (() => {
                    const key = method.id || method.name;
                    const isActive = key
                      ? methods.includes(key) ||
                        (method.name && methods.includes(method.name))
                      : false;
                    return (
                      <Chip
                        key={key ?? method.name}
                        label={method.name}
                        icon={
                          isActive ? <CheckIcon fontSize="small" /> : undefined
                        }
                        color={isActive ? "primary" : "default"}
                        variant={isActive ? "filled" : "outlined"}
                        size="small"
                        onClick={() => !readOnly && onToggleMethod(method)}
                        sx={[readOnly ? {
                          cursor: "default"
                        } : {
                          cursor: "pointer"
                        }]}
                        disabled={readOnly}
                      />
                    );
                  })(),
                )
              ) : (
                <Chip
                  label={t("coachingNavigator.sessionDetails.noMethods")}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddMethod}
            sx={{ textTransform: "none" }}
            disabled={readOnly}
          >
            {t("coachingNavigator.methods.add")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SessionContentSection;
